import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Copy, Eye, Shuffle, Wand2, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { CelebritySearch } from "../components/CelebritySearch";
import { MashFactorBanner } from "../components/MashFactorBanner";
import { CELEBRITIES } from "../data/celebrities";
import { PRIME_REASONS, primeReasonLabel } from "../data/primeReasons";
import { MASH_FACTORS, getMashFactor, DEFAULT_MASH_FACTOR_ID } from "../data/mashFactors";
import { createChallenge } from "../lib/challengeApi";

const DEFAULT_ROSTER_SIZE = 6; // Prime + 5 challengers

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const randomFill = (exclude, need) => {
  const pool = CELEBRITIES.filter((c) => !exclude.has(c.id));
  return shuffle(pool).slice(0, need).map((c) => c.id);
};

export default function CreateChallenge() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [senderName, setSenderName] = useState("");
  const [receiverName, setReceiverName] = useState("");
  const [primeId, setPrimeId] = useState(null);
  const [reason, setReason] = useState(null);
  const [customReason, setCustomReason] = useState("");
  const [mashFactorId, setMashFactorId] = useState(DEFAULT_MASH_FACTOR_ID);
  const [mode, setMode] = useState("random");
  const [category, setCategory] = useState("mixed");
  const [confidence, setConfidence] = useState(7);
  const [difficulty, setDifficulty] = useState("medium");
  const [pickedIds, setPickedIds] = useState([]); // challengers hand-picked by sender (excl prime)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishedId, setPublishedId] = useState(null);

  const primeCeleb = primeId ? CELEBRITIES.find((c) => c.id === primeId) : null;
  const receiverFirst = receiverName.trim() || "your friend";

  // Compute the full challenger list previewed to the sender.
  const finalChallengerIds = useMemo(() => {
    if (!primeId) return [];
    if (mode === "random") {
      const others = randomFill(new Set([primeId]), DEFAULT_ROSTER_SIZE - 1);
      return shuffle([primeId, ...others]);
    }
    if (mode === "curated") {
      return shuffle([primeId, ...pickedIds.filter((id) => id !== primeId)]);
    }
    // hybrid: use picked + fill to size
    const chosen = pickedIds.filter((id) => id !== primeId).slice(0, DEFAULT_ROSTER_SIZE - 1);
    const need = DEFAULT_ROSTER_SIZE - 1 - chosen.length;
    const excl = new Set([primeId, ...chosen]);
    const filler = need > 0 ? randomFill(excl, need) : [];
    return shuffle([primeId, ...chosen, ...filler]);
  }, [primeId, mode, pickedIds]);

  const previewList = finalChallengerIds.map((id) => CELEBRITIES.find((c) => c.id === id)).filter(Boolean);
  const modeOk =
    mode === "random" ||
    (mode === "curated" && pickedIds.length >= 2) ||
    (mode === "hybrid" && pickedIds.length >= 1);

  const canPublish = !!senderName.trim() && !!primeId && !!reason && (reason !== "custom" || !!customReason.trim()) && modeOk;

  const publish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    try {
      const res = await createChallenge({
        sender_name: senderName.trim(),
        receiver_name: receiverName.trim() || null,
        hidden_prime_id: primeId,
        prime_reason: reason,
        prime_reason_custom: reason === "custom" ? customReason.trim() : null,
        challenge_mode: mode,
        challenger_ids: finalChallengerIds,
        mash_factor_id: mashFactorId,
        category,
        confidence,
        difficulty,
      });
      setPublishedId(res.id);
      setStep(6);
    } catch (e) {
      toast.error("Couldn't publish challenge. Try again.");
    } finally {
      setPublishing(false);
    }
  };

  const inviteUrl = publishedId
    ? `${typeof window !== "undefined" ? window.location.origin : "https://celebritysmash.app"}/challenge/${publishedId}`
    : "";

  // ---------- Step 5 done ----------
  if (step === 6 && publishedId) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-16 pb-nav">
          <button data-testid="hp-home-btn" onClick={() => nav("/home")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
            <ArrowLeft size={16} /> Home
          </button>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#00E5FF] font-bold">Published</div>
            <h1 className="font-display text-5xl leading-[0.9] tracking-tight mt-2">
              Challenge <span className="text-[#FF107A]">Ready</span>
            </h1>
            <p className="mt-3 text-sm text-[#FDFBF7]/75">
              Your Prime is safe on the server. {receiverFirst} won&rsquo;t see it until they finish.
            </p>
            <div className="mt-6 rounded-2xl border border-[#FF107A]/40 bg-[#FF107A]/5 p-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF107A] font-bold flex items-center gap-1">
                <Lock size={12} /> Invitation link
              </div>
              <div data-testid="hp-invite-link" className="font-mono text-base mt-2 text-[#FDFBF7] break-all">
                {inviteUrl}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                data-testid="hp-copy-link-btn"
                onClick={async () => {
                  await navigator.clipboard.writeText(inviteUrl);
                  toast.success("Invitation link copied.");
                }}
                className="rounded-full bg-[#FF107A] py-3 font-bold glow-pink flex items-center justify-center gap-2"
              >
                <Copy size={16} /> Copy Link
              </button>
              <button
                data-testid="hp-preview-btn"
                onClick={() => nav(`/challenge/${publishedId}`)}
                className="rounded-full border-2 border-[#00E5FF] text-[#00E5FF] py-3 font-bold flex items-center justify-center gap-2"
              >
                <Eye size={16} /> Preview
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ---------- Render steps ----------
  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
        <button data-testid="hp-back-btn" onClick={() => (step > 1 ? setStep(step - 1) : nav("/home"))} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Back
        </button>

        {/* Step meta */}
        <div className="mt-4 flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={`step-${s}`} className={`h-1 flex-1 rounded-full ${s <= step ? "seg-active" : "bg-white/10"}`} />
          ))}
        </div>
        <div className="mt-3 text-[10px] tracking-[0.35em] uppercase text-[#00E5FF] font-bold">
          Hidden Prime · Step {step} of 5
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Names + Prime pick */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="mt-4">
              <h1 className="font-display text-4xl leading-[0.95] tracking-tight">
                Pick your <span className="text-[#FF107A]">Prime</span>.
              </h1>
              <p className="mt-2 text-sm text-[#FDFBF7]/70">
                {receiverFirst}&rsquo;s ultimate winner — the one you think they&rsquo;ll keep choosing.
              </p>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">Your name</label>
                  <input
                    data-testid="hp-sender-name"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Sarah"
                    className="mt-2 w-full rounded-xl bg-black/30 border border-white/15 px-3 py-2.5 text-[#FDFBF7] focus:outline-none focus:border-[#FF107A]"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">Friend&rsquo;s name</label>
                  <input
                    data-testid="hp-receiver-name"
                    value={receiverName}
                    onChange={(e) => setReceiverName(e.target.value)}
                    placeholder="Optional"
                    className="mt-2 w-full rounded-xl bg-black/30 border border-white/15 px-3 py-2.5 text-[#FDFBF7] focus:outline-none focus:border-[#FF107A]"
                  />
                </div>
              </div>

              <p className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">
                Who will {receiverFirst} choose above everyone else?
              </p>
              <div className="mt-2">
                {primeCeleb ? (
                  <button
                    data-testid="hp-prime-change-btn"
                    onClick={() => setPrimeId(null)}
                    className="w-full flex items-center gap-3 p-3 rounded-2xl border border-[#FF107A] bg-[#FF107A]/10 text-left"
                  >
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center font-display text-2xl shrink-0 border border-white/20"
                      style={{ background: `linear-gradient(135deg, ${primeCeleb.palette[0]}, ${primeCeleb.palette[1]})`, color: primeCeleb.palette[2] }}
                    >
                      {primeCeleb.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#FF107A] font-bold flex items-center gap-1">
                        <Lock size={10} /> Hidden Prime
                      </div>
                      <div className="font-display text-2xl leading-tight tracking-tight">{primeCeleb.name}</div>
                    </div>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/60">Change</span>
                  </button>
                ) : (
                  <CelebritySearch testId="hp-prime-search" onPick={(c) => setPrimeId(c.id)} />
                )}
              </div>

              <button
                data-testid="hp-step1-next"
                disabled={!senderName.trim() || !primeId}
                onClick={() => setStep(2)}
                className={`mt-6 w-full rounded-full py-3 font-bold flex items-center justify-center gap-2 transition-all ${
                  senderName.trim() && primeId ? "bg-[#FF107A] text-[#FDFBF7] glow-pink" : "bg-white/10 text-[#FDFBF7]/40 cursor-not-allowed"
                }`}
              >
                Next <ArrowRight size={16} />
              </button>
            </motion.div>
          )}

          {/* Step 2: Reason */}
          {step === 2 && primeCeleb && (
            <motion.div key="s2" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="mt-4">
              <h1 className="font-display text-4xl leading-[0.95] tracking-tight">
                Why will they choose <span className="text-[#FF107A]">{primeCeleb.name}</span>?
              </h1>
              <p className="mt-2 text-sm text-[#FDFBF7]/70">
                Your secret second half of the prediction.
              </p>

              <div className="mt-5 flex flex-wrap gap-2" data-testid="hp-reason-picker">
                {PRIME_REASONS.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`hp-reason-${r.id}`}
                    onClick={() => setReason(r.id)}
                    className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${
                      reason === r.id
                        ? "bg-[#FF107A] text-[#FDFBF7] border-[#FF107A] glow-pink"
                        : "bg-white/5 text-[#FDFBF7] border-white/15 hover:border-[#00E5FF]"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>

              {reason === "custom" && (
                <input
                  data-testid="hp-custom-reason"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Their custom reason…"
                  className="mt-3 w-full rounded-xl bg-black/30 border border-white/15 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#FF107A]"
                />
              )}

              <button
                data-testid="hp-step2-next"
                disabled={!reason || (reason === "custom" && !customReason.trim())}
                onClick={() => setStep(3)}
                className={`mt-6 w-full rounded-full py-3 font-bold flex items-center justify-center gap-2 transition-all ${
                  reason && (reason !== "custom" || customReason.trim())
                    ? "bg-[#FF107A] text-[#FDFBF7] glow-pink"
                    : "bg-white/10 text-[#FDFBF7]/40 cursor-not-allowed"
                }`}
              >
                Next <ArrowRight size={16} />
              </button>
            </motion.div>
          )}

          {/* Step 3: Mash Factor */}
          {step === 3 && primeCeleb && (
            <motion.div key="s3-mf" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="mt-4">
              <h1 className="font-display text-4xl leading-[0.95] tracking-tight">
                Pick the <span className="text-[#FF107A]">Mash Factor</span>.
              </h1>
              <p className="mt-2 text-sm text-[#FDFBF7]/70">
                The question {receiverFirst} answers on every matchup.
              </p>

              <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-2 max-h-[46vh] overflow-y-auto space-y-1.5" data-testid="hp-mash-factor-picker">
                {MASH_FACTORS.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`hp-mash-${f.id}`}
                    onClick={() => setMashFactorId(f.id)}
                    className={`w-full text-left rounded-xl border-2 px-3 py-2.5 transition-all ${
                      mashFactorId === f.id
                        ? "border-[#FF107A] bg-[#FF107A]/10"
                        : "border-white/10 bg-white/5 hover:border-[#00E5FF]"
                    }`}
                  >
                    <div className="font-display text-lg leading-tight tracking-tight text-[#FDFBF7]">{f.label}</div>
                  </button>
                ))}
              </div>

              <div className="mt-4">
                <MashFactorBanner label={getMashFactor(mashFactorId).label} size="lg" testId="hp-mf-preview" />
              </div>

              <button
                data-testid="hp-mash-next"
                onClick={() => setStep(4)}
                className="mt-6 w-full rounded-full py-3 font-bold bg-[#FF107A] text-[#FDFBF7] glow-pink flex items-center justify-center gap-2"
              >
                Next <ArrowRight size={16} />
              </button>
            </motion.div>
          )}

          {/* Step 4: Build challenger list */}
          {step === 4 && primeCeleb && (
            <motion.div key="s3" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="mt-4">
              <h1 className="font-display text-4xl leading-[0.95] tracking-tight">
                Build the <span className="text-[#00E5FF]">bracket</span>.
              </h1>
              <p className="mt-2 text-sm text-[#FDFBF7]/70">
                {DEFAULT_ROSTER_SIZE} celebrities total. Prime is already in.
              </p>

              <div className="mt-5 grid grid-cols-3 gap-2">
                {[
                  { id: "random", label: "Random", icon: Shuffle },
                  { id: "curated", label: "Curated", icon: Wand2 },
                  { id: "hybrid", label: "Hybrid", icon: Sparkles },
                ].map((m) => (
                  <button
                    key={m.id}
                    data-testid={`hp-mode-${m.id}`}
                    onClick={() => setMode(m.id)}
                    className={`rounded-xl border-2 py-3 px-2 text-center transition-all ${
                      mode === m.id ? "border-[#FF107A] bg-[#FF107A]/10" : "border-white/10 hover:border-[#00E5FF]"
                    }`}
                  >
                    <m.icon size={16} className="mx-auto text-[#00E5FF]" />
                    <div className="font-display text-lg tracking-tight mt-1">{m.label}</div>
                  </button>
                ))}
              </div>

              {(mode === "curated" || mode === "hybrid") && (
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">
                      Pick challengers ({pickedIds.length}/{DEFAULT_ROSTER_SIZE - 1})
                    </p>
                    <button
                      data-testid="hp-open-picker"
                      onClick={() => setPickerOpen(!pickerOpen)}
                      className="text-xs text-[#00E5FF] font-semibold"
                    >
                      {pickerOpen ? "Close" : "+ Add"}
                    </button>
                  </div>
                  {pickerOpen && (
                    <CelebritySearch
                      testId="hp-challenger-search"
                      excludeIds={[primeId, ...pickedIds]}
                      onPick={(c) => {
                        if (pickedIds.length >= DEFAULT_ROSTER_SIZE - 1) return;
                        setPickedIds([...pickedIds, c.id]);
                      }}
                    />
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {pickedIds.map((id) => {
                      const c = CELEBRITIES.find((x) => x.id === id);
                      return (
                        <button
                          key={id}
                          onClick={() => setPickedIds(pickedIds.filter((x) => x !== id))}
                          className="text-xs px-3 py-1.5 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#FDFBF7]"
                          data-testid={`hp-picked-${id}`}
                        >
                          {c?.name} ×
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Preview lineup */}
              {modeOk && previewList.length > 0 && (
                <div className="mt-6">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#A855F7] font-bold mb-2">
                    Preview lineup (shuffled)
                  </p>
                  <div data-testid="hp-preview-list" className="grid grid-cols-3 gap-2">
                    {previewList.map((c) => (
                      <div
                        key={c.id}
                        className="aspect-[3/4] rounded-lg overflow-hidden border border-white/10 relative"
                        style={{ background: `linear-gradient(135deg, ${c.palette[0]}, ${c.palette[1]})` }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="font-display text-3xl opacity-40" style={{ color: c.palette[2] }}>
                            {c.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                          </span>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/90 to-transparent">
                          <div className="text-[10px] font-semibold truncate">{c.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[10px] text-[#FDFBF7]/50 italic">
                    Your Prime is somewhere in there — {receiverFirst} won&rsquo;t know which.
                  </p>
                </div>
              )}

              <button
                data-testid="hp-step3-next"
                disabled={!modeOk}
                onClick={() => setStep(5)}
                className={`mt-6 w-full rounded-full py-3 font-bold flex items-center justify-center gap-2 transition-all ${
                  modeOk ? "bg-[#FF107A] text-[#FDFBF7] glow-pink" : "bg-white/10 text-[#FDFBF7]/40 cursor-not-allowed"
                }`}
              >
                Review <ArrowRight size={16} />
              </button>
            </motion.div>
          )}

          {/* Step 5: Confirm + publish */}
          {step === 5 && primeCeleb && (
            <motion.div key="s4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="mt-4">
              <h1 className="font-display text-4xl leading-[0.95] tracking-tight">
                Send the <span className="text-[#FF107A]">smash</span>.
              </h1>
              <p className="mt-2 text-sm text-[#FDFBF7]/70">Prime + reason stay hidden until they finish.</p>

              <div className="mt-5 rounded-2xl border border-white/10 bg-[#16141D]/80 p-4 space-y-2 text-sm">
                <Row label="Sender" value={senderName || "—"} />
                <Row label="Receiver" value={receiverName || "Anyone"} />
                <Row label="Prime" value={primeCeleb.name} accent="#FF107A" />
                <Row label="Reason" value={reason === "custom" ? customReason : primeReasonLabel(reason)} accent="#00E5FF" />
                <Row label="Mash Factor" value={getMashFactor(mashFactorId).label} accent="#A855F7" />
                <Row label="Mode" value={mode.charAt(0).toUpperCase() + mode.slice(1)} />
                <Row label="Challengers" value={`${finalChallengerIds.length} celebrities`} />
              </div>

              {/* Category */}
              <div className="mt-5">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold mb-2">Category</div>
                <div className="flex flex-wrap gap-2" data-testid="hp-category-picker">
                  {[
                    { id: "mixed", label: "Mixed" },
                    { id: "music", label: "Music Prime" },
                    { id: "movie", label: "Movie Prime" },
                    { id: "sports", label: "Sports Prime" },
                    { id: "historical", label: "Historical Prime" },
                    { id: "business", label: "Business Prime" },
                  ].map((c) => (
                    <button
                      key={c.id}
                      data-testid={`hp-category-${c.id}`}
                      onClick={() => setCategory(c.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        category === c.id ? "bg-[#FF107A] border-[#FF107A] text-[#FDFBF7]" : "bg-white/5 border-white/15"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty */}
              <div className="mt-5">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A855F7] font-bold mb-2">Difficulty · point multiplier</div>
                <div className="grid grid-cols-3 gap-2" data-testid="hp-difficulty-picker">
                  {[
                    { id: "easy", label: "Easy", mult: "1×" },
                    { id: "medium", label: "Medium", mult: "2×" },
                    { id: "hard", label: "Hard", mult: "3×" },
                  ].map((d) => (
                    <button
                      key={d.id}
                      data-testid={`hp-difficulty-${d.id}`}
                      onClick={() => setDifficulty(d.id)}
                      className={`rounded-xl border-2 py-2.5 text-center transition-all ${
                        difficulty === d.id ? "border-[#A855F7] bg-[#A855F7]/15" : "border-white/10"
                      }`}
                    >
                      <div className="font-display text-lg leading-none">{d.label}</div>
                      <div className="text-[10px] tracking-[0.2em] text-[#FDFBF7]/60 mt-1">{d.mult}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Confidence */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#FFD700] font-bold">Confidence</div>
                  <div className="font-display text-2xl text-[#FFD700] tabular" data-testid="hp-confidence-value">{confidence}/10</div>
                </div>
                <input
                  data-testid="hp-confidence-slider"
                  type="range"
                  min="1"
                  max="10"
                  value={confidence}
                  onChange={(e) => setConfidence(parseInt(e.target.value, 10))}
                  className="w-full accent-[#FFD700]"
                />
                <div className="mt-1 text-[10px] text-[#FDFBF7]/50">
                  Higher confidence = bigger bonus if you nail the Prime.
                </div>
              </div>

              <button
                data-testid="hp-publish-btn"
                disabled={!canPublish || publishing}
                onClick={publish}
                className={`mt-6 w-full rounded-full py-3 font-bold flex items-center justify-center gap-2 transition-all ${
                  canPublish && !publishing ? "bg-[#FF107A] text-[#FDFBF7] glow-pink" : "bg-white/10 text-[#FDFBF7]/40 cursor-not-allowed"
                }`}
              >
                {publishing ? "Publishing…" : "Publish Challenge"} <ArrowRight size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between items-baseline gap-3">
    <span className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/50">{label}</span>
    <span className="font-semibold text-right truncate" style={{ color: accent || "#FDFBF7" }}>{value}</span>
  </div>
);
